import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { colorHex } from '../../lib/reporteColor'
import { parseNumeric, TONAL_PALETTE, type TonalColor } from './palette'
import { formatNumero } from '../../lib/paisMapa'

export interface LineChartPoint {
  periodo: string
  valor: number | string
}

interface Props {
  data: LineChartPoint[]
  /** Color corporativo (legado). Si se da `colorNombre`, este se ignora. */
  color?: TonalColor
  /** Nombre de color Tailwind (ej. 'emerald'). Tiene prioridad sobre `color`. */
  colorNombre?: string
  /** Tono Tailwind (default '500'). Solo aplica si `colorNombre` está presente. */
  colorTono?: string
  height?: number
  formatValue?: (v: number) => string
  /** Id de configuración para el formateo de números según el país del reporte. */
  configId?: number
}

export function LineChartSerie({
  data,
  color = 'blue',
  colorNombre,
  colorTono = '500',
  height = 280,
  formatValue,
  configId,
}: Props) {
  const rows = data.map((d) => ({ periodo: d.periodo, valor: parseNumeric(d.valor) }))
  const strokeColor = colorNombre ? colorHex(colorNombre, colorTono) : TONAL_PALETTE[color]
  // ID estable basado en el color para evitar colisiones cuando hay varios gráficos en la página.
  const gradientId = `line-area-${(colorNombre ?? color)}-${colorTono}`
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <AreaChart data={rows} margin={{ top: 10, right: 24, left: 8, bottom: 10 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={strokeColor} stopOpacity={0.25} />
              <stop offset="95%" stopColor={strokeColor} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
          <XAxis dataKey="periodo" stroke="hsl(var(--muted-foreground))" fontSize={11} />
          <YAxis
            stroke="hsl(var(--muted-foreground))"
            fontSize={11}
            tickFormatter={(v) =>
              formatValue ? formatValue(v as number) : formatNumero(v as number, configId ?? 0)
            }
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'hsl(var(--background))',
              border: '1px solid hsl(var(--border))',
              borderRadius: 6,
              fontSize: 12,
            }}
            formatter={(value: number) =>
              formatValue ? formatValue(value) : formatNumero(value, configId ?? 0)
            }
          />
          <Area
            type="monotone"
            dataKey="valor"
            stroke={strokeColor}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            dot={{ r: 3, fill: strokeColor, stroke: 'white', strokeWidth: 1.5 }}
            activeDot={{ r: 5, fill: strokeColor, stroke: 'white', strokeWidth: 2 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
